import { memo, useCallback, useState, useEffect } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { changeSortOfExamList } from "store/PatientStore/actions";
import { ExamItem } from "types/Exam";
import {
  selectExamList,
  selectLoading,
  selectSortConfig,
} from "store/PatientStore/selectors";
import { getRouteLink } from "utils/general";
import { PAGES } from "constants/pages";
import { VideoExamTableHeader } from "../../components/Patient/VideoExamTableHeader";
import { VideoExamTableRow } from "../../components/Patient/VideoExamTableRow";
import { Box, CircularProgress } from "@material-ui/core";
import { Loading } from "../../../../components/Loading/Loading";
import Pagination from "@mui/material/Pagination";

export const PatientVideoExamTable = memo(() => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectLoading);
  const examList = useAppSelector(selectExamList);
  const [paginatedExams, setPaginatedExams] = useState<ExamItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const sortConfig = useAppSelector(selectSortConfig);
  const match = useRouteMatch<{ id: string }>();
  const patientLimit = useAppSelector(({ patient }) => patient.limit);
  const patientTotalExams = useAppSelector(({ patient }) => patient.totalExams);
  const loading = useAppSelector(({ patient }) => patient.loading);
  const onClick = (sortBy: string) => {
    dispatch(changeSortOfExamList(sortBy, match.params?.id));
  };
  const onLoadMore = useCallback(
    (ev, newPageRaw) => {
      setCurrentPage(newPageRaw);
      const list = Array.from(examList);
      const newPage = newPageRaw - 1;
      const offset = patientLimit * newPage;
      const start = offset;
      const end = offset + patientLimit;
      const result = list.slice(start, end);
      setPaginatedExams(result);
    },
    [currentPage, examList]
  );

  useEffect(() => {
    const list = Array.from(examList);
    setPaginatedExams(list.slice(0, patientLimit));
  }, [examList]);
  return (
    <div className="flex flex-col">
      <VideoExamTableHeader sortConfig={sortConfig} onClick={onClick} />
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <CircularProgress size={20} />
        </Box>
      ) : (
        <div className="w-full min-h-[200px] space-y-2 mt-3 flex flex-col overflow-y-hidden relative">
          {paginatedExams?.map((exam) => (
            <Link
              to={{
                pathname: `${getRouteLink(PAGES.videoReview)}${exam._id}/${
                  exam.joint._id
                }/${exam.patientId}/${exam.joint.side}`,
              }}
              key={exam.createdAt}
            >
              <VideoExamTableRow
                jointName={exam.joint.name}
                side={exam.joint?.side}
                painScale={exam.painScaleValue}
                survey={exam.outcomeScore}
                type={exam.movement.name}
                rightMin={exam.examMLResult.rightMinValue?.toFixed(2) ?? "-"}
                rightMax={exam.examMLResult.rightMaxValue?.toFixed(2) ?? "-"}
                leftMin={exam.examMLResult.leftMinValue?.toFixed(2) ?? "-"}
                leftMax={exam.examMLResult.leftMaxValue?.toFixed(2) ?? "-"}
                range={0}
                createdDate={exam.createdAt}
              />
            </Link>
          ))}
          <Loading loading={loading} states={paginatedExams} />
        </div>
      )}
      <Box display="flex" justifyContent="flex-end" marginTop={4}>
        <Pagination
          variant="outlined"
          shape="rounded"
          page={currentPage}
          count={Math.ceil(patientTotalExams / patientLimit)}
          onChange={onLoadMore}
        />
      </Box>
    </div>
  );
});
